import {
  type ConsentOptions,
  type EventData,
  LayersClient,
  type LayersConfig,
  type RemoteConfig,
  type UserProperties
} from '@layers/client';
import { Platform } from 'react-native';

import type { ATTEventData, ATTPermissionOptions, ATTStatus } from './native/ATTModule';
import type {
  ConnectorCapabilities,
  ConnectorConfig,
  ConnectorMetrics,
  ConnectorStatus,
  EventMappingRule,
  NetworkEvent,
  NetworkType
} from './native/ConnectorsModule';
import type {
  AttributionData,
  DeepLinkConfig,
  DeepLinkData,
  DeepLinkListener,
  DeepLinkMetrics
} from './native/DeepLinkModule';
import {
  type SKANConversionRule,
  type SKANEventData,
  SKANManager,
  type SKANMetrics,
  type SKANPostback,
  type SKANPresetConfig,
  type SKANVersion
} from './native/SKANModule';
import { getOrSetInstallId } from './storage/identity';
import { createReactNativeQueueStorage } from './storage/nativeQueueStorage';

export type {
  ATTStatus,
  ATTPermissionOptions,
  ATTEventData,
  DeepLinkData,
  AttributionData,
  DeepLinkListener,
  DeepLinkMetrics,
  ConnectorConfig,
  ConnectorCapabilities,
  ConnectorStatus,
  ConnectorMetrics,
  NetworkType,
  EventMappingRule,
  NetworkEvent,
  SKANVersion,
  SKANConversionRule,
  SKANPresetConfig,
  SKANPostback,
  SKANEventData,
  SKANMetrics
};

const SUPPORTED_NETWORKS: NetworkType[] = ['meta', 'tiktok', 'snap', 'google'];

const DEFAULT_DEEP_LINK_CONFIG: DeepLinkConfig = {
  urlSchemes: [],
  universalLinkDomains: [],
  deferredDeepLinkTimeout: 0,
  attributionWindowDays: 0,
  trackOrganicLinks: false
};

const EMPTY_CONNECTOR_STATUS: ConnectorStatus = {
  network: 'meta',
  enabled: false,
  initialized: false,
  hasSDK: false,
  eventsSent: 0,
  errors: 0
};

const EMPTY_CONNECTOR_METRICS: ConnectorMetrics = {
  network: 'meta',
  totalEvents: 0,
  successfulEvents: 0,
  failedEvents: 0
};

const EMPTY_DEEP_LINK_METRICS: DeepLinkMetrics = {
  totalLinks: 0,
  uniqueLinks: 0,
  attributedInstalls: 0,
  attributedSessions: 0,
  deferredLinks: 0,
  sourceBreakdown: {},
  conversionRate: 0
};

const NOT_SUPPORTED_ATT_STATUS: ATTStatus = 'not_determined';

const NOT_SUPPORTED_SKAN_METRICS: SKANMetrics = {
  isSupported: false,
  version: '4.0',
  currentValue: null,
  currentPreset: null,
  ruleCount: 0,
  evaluationCount: 0,
  attributionData: null,
  lastEvaluations: []
};

class LayersRN {
  private client: LayersClient | null = null;
  private isInitialized = false;
  private deepLinkConfig: DeepLinkConfig | null = null;
  private skanManager: SKANManager | null = null;

  async init(config: LayersConfig): Promise<void> {
    const installId = await getOrSetInstallId();

    this.client = new LayersClient({
      ...config,
      queueStorage: createReactNativeQueueStorage(config.appId),
      queueOptions: {
        flushIntervalMs: 1_000,
        maxQueueSize: 50,
        maxItemAgeMs: 60_000,
        requestTimeoutMs: 10_000,
        maxRetries: 1,
        baseRetryDelayMs: 500,
        maxRetryDelayMs: 1_000,
        ...(config.queueOptions ?? {})
      }
    });

    this.client.setDeviceInfo({
      platform:
        Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'react-native',
      os_version:
        typeof Platform.Version === 'string' ? Platform.Version : String(Platform.Version),
      install_id: installId
    });

    await this.client.init();

    // Initialize SKAN on iOS
    if (Platform.OS === 'ios') {
      await this.initializeSKAN();
    }

    this.isInitialized = true;

    if (config.enableDebug) {
      console.log('Layers initialized with SKAN support.');
    }

    // TEST: Automatically test TikTok API on init
  }

  /**
   * Initialize SKAN and apply config from server
   */
  private async initializeSKAN(): Promise<void> {
    try {
      // Create SKAN manager with conversion update callback
      this.skanManager = new SKANManager((data: SKANEventData) => {
        // Log SKAN conversion updates for debugging
        if (this.client?.getConfig().enableDebug) {
          console.log('[Layers SKAN] Conversion updated:', {
            event: data.event,
            previousValue: data.previousValue,
            newValue: data.newValue,
            rule: data.matchedRule?.description
          });
        }
      });

      await this.skanManager.initialize();

      // Fetch and apply SKAN config from server
      const remoteConfig = await this.getRemoteConfig();
      if (remoteConfig?.skan) {
        await this.applySKANConfig(remoteConfig.skan);
      }
    } catch (error) {
      console.warn('[Layers] SKAN initialization failed:', error);
    }
  }

  /**
   * Get remote config from client
   */
  private async getRemoteConfig(): Promise<RemoteConfig | null> {
    if (!this.client) return null;
    try {
      // Access private field via getClient for now
      // TODO: Add public getRemoteConfig() method to LayersClient
      return (this.client as any).remoteConfig ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Apply SKAN configuration from server
   */
  private async applySKANConfig(skanConfig: any): Promise<void> {
    if (!this.skanManager || !skanConfig.enabled) {
      return;
    }

    try {
      if (skanConfig.preset && ['subscriptions', 'engagement', 'iap'].includes(skanConfig.preset)) {
        this.skanManager.setPreset(skanConfig.preset);
        console.log(`[Layers SKAN] Preset applied: ${skanConfig.preset}`);
      } else if (Array.isArray(skanConfig.customRules) && skanConfig.customRules.length > 0) {
        this.skanManager.setCustomRules(skanConfig.customRules);
        console.log(`[Layers SKAN] Custom rules applied: ${skanConfig.customRules.length} rules`);
      }
    } catch (error) {
      console.warn('[Layers] Failed to apply SKAN config:', error);
    }
  }

  getClient(): LayersClient | null {
    return this.client;
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  async destroy(): Promise<void> {
    if (this.client) {
      await this.client.flush();
      this.client = null;
    }
    this.isInitialized = false;
  }

  async track(eventName: string, properties: EventData = {}): Promise<void> {
    if (!this.client) {
      throw new Error('Layers must be initialized before tracking events');
    }

    const eventProperties = {
      platform: Platform.OS,
      ...properties
    };

    // Track event to Layers backend
    await this.client.track(eventName, eventProperties);

    // Process event through SKAN on iOS (fire and forget)
    if (Platform.OS === 'ios' && this.skanManager) {
      this.skanManager.processEvent(eventName, eventProperties).catch((error) => {
        if (this.client?.getConfig().enableDebug) {
          console.warn('[Layers SKAN] Event processing failed:', error);
        }
      });
    }
  }

  async screen(screenName: string, properties: EventData = {}): Promise<void> {
    await this.track('screen_view', { screen_name: screenName, ...properties });
  }

  async setUserProperties(properties: UserProperties): Promise<void> {
    if (!this.client) {
      throw new Error('Layers must be initialized before setting user properties');
    }
    await this.client.setUserProperties(properties);
  }

  setAppUserId(appUserId: string): void {
    if (!this.client) {
      throw new Error('Layers must be initialized before setting app user ID');
    }
    this.client.setAppUserId(appUserId);
  }

  clearAppUserId(): void {
    if (!this.client) {
      throw new Error('Layers must be initialized before clearing app user ID');
    }
    this.client.setAppUserId(undefined);
  }

  async setConsent(consent: ConsentOptions): Promise<void> {
    if (!this.client) {
      throw new Error('Layers must be initialized before setting consent');
    }
    await this.client.setConsent(consent);
  }

  async requestTracking(_options?: { strategy?: string }): Promise<void> {
    console.warn('Layers ATT support is disabled in simplified mode.');
  }

  debug = {
    showOverlay: (_options?: { position?: 'top' | 'bottom'; theme?: 'light' | 'dark' }) => {
      console.warn('Debug overlay is not available in simplified mode.');
    },
    hideOverlay: () => {
      /* no-op */
    },
    isOverlayVisible: () => false,
    getATTStatus: async () => NOT_SUPPORTED_ATT_STATUS,
    getSKANMetrics: async (): Promise<SKANMetrics> => {
      if (Platform.OS !== 'ios' || !this.skanManager) {
        return NOT_SUPPORTED_SKAN_METRICS;
      }
      return this.skanManager.getSKANMetrics();
    },
    getDeepLinkMetrics: async () => EMPTY_DEEP_LINK_METRICS,
    getConnectorStatuses: async () => this.createConnectorStatusMap(),
    testConnectorConnection: async (_network: NetworkType) => false,
    flushAllEvents: async () => {
      if (this.client) {
        await this.client.flush();
      }
      return this.createZeroCountMap();
    }
  };

  connectors = {
    configure: async (_network: NetworkType, _config: Partial<ConnectorConfig>) => false,
    enable: async (_network: NetworkType) => false,
    disable: async (_network: NetworkType) => false,
    getStatus: async (network: NetworkType) => ({
      ...EMPTY_CONNECTOR_STATUS,
      network
    }),
    getAllStatuses: async () => this.createConnectorStatusMap(),
    getMetrics: async (network: NetworkType) => ({
      ...EMPTY_CONNECTOR_METRICS,
      network
    }),
    testConnection: async (_network: NetworkType) => false,
    flush: async () => this.createZeroCountMap()
  };

  deepLinks = {
    configure: async (config: Partial<DeepLinkConfig>) => {
      const merged = {
        ...DEFAULT_DEEP_LINK_CONFIG,
        ...(this.deepLinkConfig ?? {}),
        ...config
      };
      this.deepLinkConfig = merged;
    },
    addListener: (_listener: DeepLinkListener) => {
      console.warn('Deep link listeners are not available in simplified mode.');
      return () => undefined;
    },
    getMetrics: async () => EMPTY_DEEP_LINK_METRICS,
    parseUrl: (url: string) => {
      try {
        const parsed = new URL(url);
        const params: Record<string, string> = {};
        parsed.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        const data: DeepLinkData = {
          url,
          scheme: parsed.protocol.replace(':', ''),
          host: parsed.hostname,
          path: parsed.pathname,
          queryParams: params,
          timestamp: Date.now()
        };
        if (parsed.hash) {
          data.fragment = parsed.hash.slice(1);
        }
        return data;
      } catch {
        throw new Error(`Invalid deep link URL: ${url}`);
      }
    },
    clearAttribution: async () => {
      /* no-op */
    }
  };

  att = {
    request: async (_strategy?: string) => {
      console.warn('ATT request is not supported in simplified mode.');
      return NOT_SUPPORTED_ATT_STATUS;
    },
    getStatus: async () => NOT_SUPPORTED_ATT_STATUS,
    isSupported: async () => false,
    setCustomPrompt: (_title: string, _message: string) => {
      console.warn('ATT custom prompts are not supported in simplified mode.');
    }
  };

  skan = {
    setPreset: async (preset: SKANPresetConfig['name']) => {
      if (Platform.OS !== 'ios') {
        console.warn('[Layers SKAN] Only supported on iOS');
        return;
      }
      if (!this.skanManager) {
        console.warn('[Layers SKAN] Not initialized');
        return;
      }
      this.skanManager.setPreset(preset);
    },
    setRules: async (rules: SKANConversionRule[]) => {
      if (Platform.OS !== 'ios') {
        console.warn('[Layers SKAN] Only supported on iOS');
        return;
      }
      if (!this.skanManager) {
        console.warn('[Layers SKAN] Not initialized');
        return;
      }
      this.skanManager.setCustomRules(rules);
    },
    getCurrentConversionValue: async () => {
      if (Platform.OS !== 'ios' || !this.skanManager) {
        return null;
      }
      return this.skanManager.getCurrentValue();
    },
    getMetrics: async (): Promise<SKANMetrics> => {
      if (Platform.OS !== 'ios' || !this.skanManager) {
        return NOT_SUPPORTED_SKAN_METRICS;
      }
      return this.skanManager.getSKANMetrics();
    }
  };

  private createConnectorStatusMap(): Record<NetworkType, ConnectorStatus> {
    const statuses: Record<NetworkType, ConnectorStatus> = {} as Record<
      NetworkType,
      ConnectorStatus
    >;
    for (const network of SUPPORTED_NETWORKS) {
      statuses[network] = { ...EMPTY_CONNECTOR_STATUS, network };
    }
    return statuses;
  }

  private createZeroCountMap(): Record<NetworkType, number> {
    return {
      meta: 0,
      tiktok: 0,
      snap: 0,
      google: 0
    };
  }
}

export const Layers = new LayersRN();
export default Layers;

export interface DebugOverlayProps {
  visible: boolean;
  onClose: () => void;
  layersClient?: LayersClient | null;
}

export const DebugOverlay = (_props: DebugOverlayProps) => null;

export * from '@layers/client';
export * from './ExpoRouterIntegration';
export * from './integrations/Commerce';
export * from './integrations/RevenueCat';
export * from './integrations/Superwall';
